import mixpanel from 'mixpanel-browser';
import userStore from "../Stores/User";

/**
 * Extracts tracking parameters from URL including UTM params and other common tracking params
 * @returns {Object} Object containing all tracking parameters found in URL
 */
export const getTrackingParams = () => {
  const params = new URLSearchParams(window.location.search);
  const trackingParams = {};

  // UTM parameters - store with initial_ prefix
  const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  utmParams.forEach(param => {
    const value = params.get(param);
    if (value) {
      // Store both original and initial_ version
      trackingParams[param] = value;
      trackingParams[`initial_${param.replace('utm_', '')}`] = value;
    }
  });

  // Common tracking parameters
  const commonParams = [
    'ref', 'source', 'referrer', 'origin', 'channel', 
    'campaign', 'ad_id', 'adset_id', 'adgroup_id',
    'placement', 'creative', 'device', 'platform'
  ];

  commonParams.forEach(param => {
    const value = params.get(param);
    if (value) trackingParams[param] = value;
  });

  // Add referrer if not already in params
  if (!trackingParams.referrer && document.referrer) {
    trackingParams.referrer = document.referrer;
    trackingParams.initial_referrer = document.referrer;
  }

  // Note: Landing page should only be set explicitly by landing pages
  // using setLandingPage() function, not automatically from pathname
  // This prevents onboarding/other pages from being incorrectly recorded as landing pages

  console.log('trackingParams', trackingParams);
  return trackingParams;
};

/**
 * Sets tracking parameters in mixpanel if they don't already exist
 * Called on app entry to capture UTM params
 */
export const setTrackingParams = () => {
  const trackingParams = getTrackingParams();

  if (userStore?.user?.user_id) {
    mixpanel.identify(userStore.user.user_id);
    const peopleProps = { 
      ...(userStore.user.email && { email: userStore.user.email }),
      ...(userStore.user.name && { name: userStore.user.name }),
      ...(userStore.user.displayName && { displayName: userStore.user.displayName }),
      ...(userStore.user.photoURL && { photoURL: userStore.user.photoURL })
    };
    // Set user props that can be updated
    mixpanel.people.set(peopleProps);
    // Set UTM params only once
    mixpanel.people.set_once(trackingParams);
    mixpanel.register_once(trackingParams);
    localStorage.setItem('utm_params', JSON.stringify(trackingParams));
  } else {
    mixpanel.identify(mixpanel.get_distinct_id());
    // Set UTM params only once
    mixpanel.people.set_once(trackingParams);
    mixpanel.register_once(trackingParams);
    localStorage.setItem('utm_params', JSON.stringify(trackingParams));
  }
};

/**
 * Sets landing page tracking in mixpanel
 * Called from landing pages to set utm_landingpage
 * @param {string} pageName - Name of the landing page (e.g. 'landing_a', 'feed_landing')
 */
export const setLandingPage = (pageName) => {
  const abTestVariant = localStorage.getItem('landing_page_variant');
  let finalPageName = pageName;
  
  // If no pageName provided, derive it from current path
  if (!finalPageName) {
    const path = window.location.pathname;
    const pathParts = path.slice(1).split('/');
    finalPageName = pathParts[0] || 'landing';
    
    // If it's a landing page and we have an AB test variant, use that
    if (finalPageName === 'landing' && abTestVariant) {
      const variantLetter = abTestVariant.toLowerCase().replace('variant', '');
      finalPageName = `landing_${variantLetter}`;
    }
  }

  const landingParams = {
    utm_landingpage: finalPageName,
    initial_landingpage: finalPageName
  };

  if (userStore?.user?.user_id) {
    mixpanel.identify(userStore.user.user_id);
    // Use set_once to ensure first landing page is preserved
    mixpanel.people.set_once(landingParams);
    mixpanel.register_once(landingParams);
    // Only set localStorage if it doesn't exist yet
    if (!localStorage.getItem('utm_landingpage')) {
      localStorage.setItem('utm_landingpage', finalPageName);
    }
  } else {
    mixpanel.identify(mixpanel.get_distinct_id());
    // Use set_once to ensure first landing page is preserved
    mixpanel.people.set_once(landingParams);
    mixpanel.register_once(landingParams);
    // Only set localStorage if it doesn't exist yet
    if (!localStorage.getItem('utm_landingpage')) {
      localStorage.setItem('utm_landingpage', finalPageName);
    }
  }
}; 